import type {
  AdditionalLenderQuestions,
  AdditionalQuestionCompany,
} from "@/models/underwriting";

export const emptyAdditionalLenderQuestions = (): AdditionalLenderQuestions => ({
  grossAnnualIncome: null,
  additionalAnnualIncome: null,
  rentMortgageAmount: null,
  utilitiesHouseholdPayments: null,
  insuranceSubscriptionPayments: null,
  creditCardLoanPayments: null,
  dependents: null,
  occupationSector: null,
  nationalityOnLicence: null,
  drivingLicenceNumber: null,
  circumstancesChange: null,
  circumstancesChangeReason: null,
  replacementLoan: null,
  replacementLoanAmount: null,
  existingLender: null,
  replacementLoanMonthlyPayment: null,
  businessUse: null,
  affordFinanceWithCurrentIncome: null,
  incomeNotExpectedToChange: null,
  expenditureNotExpectedToChange: null,
  vwfsContactByEmail: null,
  vwfsContactByPhone: null,
  vwfsContactBySMS: null,
  vwfsContactByPost: null,
  requiresAdditionalSupport: null,
  additionalSupportDetails: null,
  hasMajorityShareholdingInterest: null,
  shareholdingCompanies: [],
  isPartnerOfPartnership: null,
  partnershipCompanies: [],
  consentToHardCreditSearch: null,
  agreeToVWFSPrivacyPolicy: null,
  vwfsOccupation: null,
  vwfsPreviousOccupation: null,
});

const normaliseCompany = (raw: unknown): AdditionalQuestionCompany => {
  if (typeof raw !== "object" || raw === null) return { companyName: "", companyNumber: "" };
  const o = raw as Record<string, unknown>;
  return {
    companyName: typeof o.companyName === "string" ? o.companyName : "",
    companyNumber: typeof o.companyNumber === "string" ? o.companyNumber : "",
  };
};

/** The gateway stores the company rows as a JSON string column. */
const parseCompanyJson = (value: unknown): AdditionalQuestionCompany[] => {
  if (value == null || !String(value).trim()) return [];
  try {
    const parsed = JSON.parse(String(value)) as unknown;
    return Array.isArray(parsed) ? parsed.map(normaliseCompany) : [];
  } catch {
    return [];
  }
};

/** The API uses `-1` as its "not answered" sentinel for the two free-text-backed selects. */
const fromSentinel = (value: unknown): string | null =>
  value == null || value === -1 || value === "-1" ? null : String(value);

export function parseAdditionalLenderQuestions(
  raw: Record<string, unknown> | null | undefined
): AdditionalLenderQuestions | null {
  if (raw == null) return null;
  const { shareholdingCompany, partnershipRegistration, ...rest } = raw;
  return {
    ...emptyAdditionalLenderQuestions(),
    ...(rest as Partial<AdditionalLenderQuestions>),
    nationalityOnLicence: fromSentinel(rest.nationalityOnLicence),
    existingLender: fromSentinel(rest.existingLender),
    shareholdingCompanies: parseCompanyJson(shareholdingCompany),
    partnershipCompanies: parseCompanyJson(partnershipRegistration),
  };
}

export function serializeAdditionalLenderQuestions(
  model: AdditionalLenderQuestions
): Record<string, unknown> {
  const { shareholdingCompanies, partnershipCompanies, ...rest } = model;
  return {
    ...rest,
    shareholdingCompany:
      model.hasMajorityShareholdingInterest === true
        ? JSON.stringify(shareholdingCompanies ?? [])
        : null,
    partnershipRegistration:
      model.isPartnerOfPartnership === true ? JSON.stringify(partnershipCompanies ?? []) : null,
  };
}

const nonEmpty = (v?: string | null) => !!(v && v.trim().length > 0);

const companiesComplete = (rows: AdditionalQuestionCompany[] | undefined) =>
  !!rows?.length && rows.every((c) => nonEmpty(c.companyName) && nonEmpty(c.companyNumber));

/**
 * Validation for the pre-propose questions. Errors are keyed by model field so the
 * dialog can highlight the offending control. VWFS questions are only validated when
 * the quote results include a VWFS lender.
 */
export function getAdditionalQuestionErrors(
  q: AdditionalLenderQuestions,
  includeVwfs: boolean,
  requirePreviousOccupation = true
): Record<string, string> {
  const errors: Record<string, string> = {};

  if (q.grossAnnualIncome == null || q.grossAnnualIncome <= 0) {
    errors.grossAnnualIncome = "Gross annual income is required";
  }
  if (q.additionalAnnualIncome == null) {
    errors.additionalAnnualIncome = "Additional annual income is required";
  }
  if (q.rentMortgageAmount == null) errors.rentMortgageAmount = "Rent/Mortgage is required";
  if (q.utilitiesHouseholdPayments == null) {
    errors.utilitiesHouseholdPayments = "Utilities/household bills are required";
  }
  if (q.insuranceSubscriptionPayments == null) {
    errors.insuranceSubscriptionPayments = "Insurance/subscription payments are required";
  }
  if (q.dependents == null) errors.dependents = "Number of dependants is required";
  if (!nonEmpty(q.occupationSector)) errors.occupationSector = "Occupation sector is required";
  if (!nonEmpty(q.nationalityOnLicence)) {
    errors.nationalityOnLicence = "Nationality on licence is required";
  }
  if (q.circumstancesChange == null) {
    errors.circumstancesChange = "Please answer the foreseeable changes question";
  }
  if (q.circumstancesChange === true && !nonEmpty(q.circumstancesChangeReason)) {
    errors.circumstancesChangeReason = "Please provide a reason for the change";
  }
  if (q.replacementLoan == null) {
    errors.replacementLoan = "Please confirm if this is a replacement loan";
  }
  if (q.replacementLoan === true) {
    if (q.replacementLoanAmount == null || q.replacementLoanAmount <= 0) {
      errors.replacementLoanAmount = "Replacement loan amount must be greater than 0";
    }
    if (!nonEmpty(q.existingLender)) errors.existingLender = "Existing lender is required";
    if (q.replacementLoanMonthlyPayment == null || q.replacementLoanMonthlyPayment <= 0) {
      errors.replacementLoanMonthlyPayment =
        "Replacement loan monthly payment must be greater than 0";
    }
  }

  if (!includeVwfs) return errors;

  if (q.affordFinanceWithCurrentIncome == null) {
    errors.affordFinanceWithCurrentIncome = "This question is required";
  }
  if (q.incomeNotExpectedToChange == null) {
    errors.incomeNotExpectedToChange = "This question is required";
  }
  if (q.expenditureNotExpectedToChange == null) {
    errors.expenditureNotExpectedToChange = "This question is required";
  }
  if (q.requiresAdditionalSupport == null) {
    errors.requiresAdditionalSupport = "This question is required";
  }
  if (q.requiresAdditionalSupport === true && !nonEmpty(q.additionalSupportDetails)) {
    errors.additionalSupportDetails = "Please provide details";
  }
  if (q.hasMajorityShareholdingInterest == null) {
    errors.hasMajorityShareholdingInterest = "This question is required";
  }
  if (q.hasMajorityShareholdingInterest === true && !companiesComplete(q.shareholdingCompanies)) {
    errors.shareholdingCompanies =
      "Enter each company registered name and registration number";
  }
  if (q.isPartnerOfPartnership == null) {
    errors.isPartnerOfPartnership = "This question is required";
  }
  if (q.isPartnerOfPartnership === true && !companiesComplete(q.partnershipCompanies)) {
    errors.partnershipCompanies =
      "Enter each partnership registered name and registration number";
  }
  if (q.consentToHardCreditSearch !== true) {
    errors.consentToHardCreditSearch = "You must confirm to proceed";
  }
  if (q.agreeToVWFSPrivacyPolicy !== true) {
    errors.agreeToVWFSPrivacyPolicy = "You must confirm to proceed";
  }
  if (!nonEmpty(q.vwfsOccupation)) errors.vwfsOccupation = "Occupation sector is required";
  if (requirePreviousOccupation && !nonEmpty(q.vwfsPreviousOccupation)) {
    errors.vwfsPreviousOccupation = "Previous occupation sector is required";
  }

  return errors;
}
